// The 3D modules (both Rapid Tracking tests, Spatial Integration, Auditory
// Capacity and Trace Test 1) draw with three.js, which needs WebGL 2. When a
// browser can't start it -- an older browser or graphics chip with WebGL 1
// only, hardware acceleration switched off, or the browser blocking 3D on a
// site after a graphics hiccup -- the renderer can't be created and the
// scene would stay blank.

// A renderer with the requested options, or, if the graphics chip refuses
// those, one without antialiasing on the default GPU, which weaker chips
// can often still manage. Throws when neither can be created.
export const createWebGLRenderer = (THREE, options = {}) => {
  try {
    return new THREE.WebGLRenderer(options);
  } catch (error) {
    const lighter = { ...options, antialias: false, powerPreference: 'default' };
    if (options.antialias === lighter.antialias && (options.powerPreference || 'default') === 'default') throw error;
    return new THREE.WebGLRenderer(lighter);
  }
};

const canCreateContext = (type) => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext(type);
    if (!context) return false;
    // Hand the context straight back: browsers allow only a few at once.
    context.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch (error) {
    return false;
  }
};

export const getWebGLSupport = () => ({
  webgl2: canCreateContext('webgl2'),
  webgl1: canCreateContext('webgl')
});

// Calls onLost when the canvas's context is lost and the browser hasn't
// given it back within graceMs (a brief loss is often restored). Returns a
// function that stops watching; call it before disposing of the renderer,
// since forceContextLoss() is a context loss too.
export const watchWebGLContext = (canvas, onLost, graceMs = 2500) => {
  let timer = null;
  const handleLost = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      onLost();
    }, graceMs);
  };
  const handleRestored = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  canvas.addEventListener('webglcontextlost', handleLost, false);
  canvas.addEventListener('webglcontextrestored', handleRestored, false);
  return () => {
    if (timer) clearTimeout(timer);
    timer = null;
    canvas.removeEventListener('webglcontextlost', handleLost, false);
    canvas.removeEventListener('webglcontextrestored', handleRestored, false);
  };
};
